import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

/**
 * Axios Instance Configuration
 *
 * Pre-configured Axios client with:
 * - Base URL from environment variable
 * - Automatic JWT token attachment
 * - Token refresh on 401 errors
 * - Request/response interceptors
 * - Error handling
 */

// Get API base URL from environment
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

/**
 * Session Storage Interface
 *
 * Abstract session storage to allow different implementations
 * (in-memory, sessionStorage, etc.)
 */
interface SessionStorage {
  getToken: () => string | null;
  getRefreshToken: () => string | null;
  setToken: (token: string) => void;
  clearTokens: () => void;
}

// In-memory session storage (more secure than localStorage)
let sessionToken: string | null = null;
let sessionRefreshToken: string | null = null;

export const sessionStorage: SessionStorage = {
  getToken: () => sessionToken,
  getRefreshToken: () => sessionRefreshToken,
  setToken: (token: string) => {
    sessionToken = token;
  },
  clearTokens: () => {
    sessionToken = null;
    sessionRefreshToken = null;
  },
};

/**
 * Axios instance with interceptors
 */
export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 second timeout
});

/**
 * Request Interceptor
 *
 * Automatically attach Authorization header with JWT token to all requests
 */
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = sessionStorage.getToken();

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

/**
 * Response Interceptor
 *
 * Handle common error scenarios:
 * - 401 Unauthorized: Token expired, redirect to login
 * - Network errors: Show user-friendly message
 * - 5xx errors: Server error handling
 */
api.interceptors.response.use(
  (response) => {
    // Pass through successful responses
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Handle 401 Unauthorized (token expired)
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Attempt token refresh
      const refreshToken = sessionStorage.getRefreshToken();

      if (refreshToken) {
        try {
          // Call refresh endpoint
          const response = await axios.post(`${API_BASE_URL}/v1/auth/refresh`, {
            refresh_token: refreshToken,
          });

          const { token } = response.data;

          // Update stored token
          sessionStorage.setToken(token);

          // Retry original request with new token
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }

          return api(originalRequest);
        } catch (refreshError) {
          // Refresh failed - clear tokens and redirect to login
          sessionStorage.clearTokens();

          // Redirect to login page (if not already there and not in test environment)
          if (
            typeof window !== 'undefined' &&
            window.location.pathname !== '/login' &&
            import.meta.env.MODE !== 'test'
          ) {
            window.location.href = '/login';
          }

          return Promise.reject(refreshError);
        }
      } else {
        // No refresh token - redirect to login
        sessionStorage.clearTokens();

        if (
          typeof window !== 'undefined' &&
          window.location.pathname !== '/login' &&
          import.meta.env.MODE !== 'test'
        ) {
          window.location.href = '/login';
        }

        return Promise.reject(error);
      }
    }

    // Handle network errors
    if (error.message === 'Network Error') {
      console.error('Network error: Unable to reach API server');
      // Let the calling component handle this (show offline banner, etc.)
    }

    // Handle timeout errors
    if (error.code === 'ECONNABORTED') {
      console.error('Request timeout: API server did not respond in time');
    }

    // Pass through other errors to calling code
    return Promise.reject(error);
  }
);

/**
 * Typed API Error Response
 *
 * Standardized error format from backend API
 */
export interface ApiError {
  error: string; // Error code (UPPERCASE_SNAKE_CASE)
  message: string; // Human-readable error message
  details?: Record<string, unknown>; // Optional additional context
  timestamp: string; // ISO 8601 timestamp
}

/**
 * Type guard for API errors
 */
export function isApiError(error: unknown): error is AxiosError<ApiError> {
  return (
    axios.isAxiosError(error) &&
    error.response !== undefined &&
    typeof error.response.data === 'object' &&
    error.response.data !== null &&
    'error' in error.response.data &&
    'message' in error.response.data
  );
}

/**
 * Extract user-friendly error message from API error
 */
export function getErrorMessage(error: unknown): string {
  if (isApiError(error) && error.response) {
    return error.response.data.message;
  }

  if (axios.isAxiosError(error)) {
    if (error.message === 'Network Error') {
      return 'Unable to connect to server. Please check your internet connection.';
    }

    if (error.code === 'ECONNABORTED') {
      return 'Request timed out. Please try again.';
    }

    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'An unexpected error occurred. Please try again.';
}
